import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys, type QueryKeyPart } from "@nasam/kernel";
import type {
  GprsCatalogQuery,
  GprsCommand,
  GprsCommandsListQuery,
  GprsCommandsRepository,
  SendGprsCommandInput,
} from "../domain";

/** While a shown command is still PENDING, look again this often, so its end
 *  (Successful, Failed, or Timed Out after 60 s) shows without a refresh.
 *  There's no socket event for it: the API says to poll. */
export const PENDING_POLL_INTERVAL_MS = 5_000;

function params(
  query: GprsCommandsListQuery | undefined,
): Readonly<Record<string, QueryKeyPart>> | undefined {
  return query === undefined ? undefined : { ...query };
}

export function useGprsCommandsQuery(
  repository: GprsCommandsRepository,
  query?: GprsCommandsListQuery,
) {
  return useQuery({
    queryKey: queryKeys.gprsCommands.list(params(query)),
    queryFn: () => repository.list(query),
    refetchInterval: (current) =>
      current.state.data?.data.some((row) => row.status === "PENDING")
        ? PENDING_POLL_INTERVAL_MS
        : false,
  });
}

/** One command, starting from the list's copy and followed while PENDING. */
export function useGprsCommandQuery(
  repository: GprsCommandsRepository,
  initial: GprsCommand | null,
) {
  return useQuery({
    queryKey: queryKeys.gprsCommands.detail(initial?.id ?? ""),
    queryFn: () => repository.get(initial!.id),
    enabled: initial !== null,
    ...(initial === null ? {} : { initialData: initial, initialDataUpdatedAt: 0 }),
    refetchInterval: (current) =>
      current.state.data?.status === "PENDING" ? PENDING_POLL_INTERVAL_MS : false,
  });
}

/** The commands the chosen targets take; every command with none chosen. */
export function useGprsCatalogQuery(
  repository: GprsCommandsRepository,
  query: GprsCatalogQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.gprsCommands.all,
      "catalog",
      [...(query.deviceIds ?? [])].sort().join(","),
      [...(query.accountIds ?? [])].sort().join(","),
    ],
    queryFn: () => repository.catalog(query),
    enabled,
    staleTime: 60_000,
  });
}

export function useSendGprsCommandMutation(repository: GprsCommandsRepository) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SendGprsCommandInput) => repository.send(input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.gprsCommands.lists() });
    },
  });
}
